import {
  Box,
  Button,
  Code,
  Flex,
  Heading,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { LexicalEditor } from "lexical";
import { sum } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RiLightbulbLine } from "react-icons/ri";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { NotFound } from "src/components/Feedback/NotFound";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import {
  RecipientsTable,
  RecipientsTableDataType,
} from "src/components/Table/RecipientsTable";
import { EditorRef } from "src/components/WysiwygEditor/lexical/Editor";
import {
  convertPlainTextToHtml,
  editorStateToHtml,
  editorStateToHtmlWithCIDs,
  editorStateToPlainText,
  importHtml,
  importPlainText,
} from "src/components/WysiwygEditor/lexical/utils/converters";
import { $validateEditorState } from "src/components/WysiwygEditor/lexical/utils/validator";
import {
  MAX_EMAIL_ATTACHMENTS_SIZE_MB,
  MB_SIZE,
  MESSAGE_CONTENT_LIMIT,
} from "src/constants";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useGetMessageRecipients } from "src/hooks/useGetMessageRecipients";
import { useMessageRichContent } from "src/hooks/useMessageRichContent";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { toSegmentedMessage } from "src/hooks/useSegmentedMessage";
import { NewMessageState } from "src/hooks/useSendMessage";
import useUser from "src/hooks/useUser";
import { generateMessageSchema } from "src/schemas/Message";
import * as breadcrumb from "src/services/breadcrumb";
import { validateWithZod } from "src/services/formValidations";
import { mustacheSafeParse } from "src/services/mustache";
import * as Person from "src/services/person";
import { Status } from "src/types/authData";
import { FormikErrorAccumulator } from "src/types/FormikErrorsAccumulator";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { MessageForm } from "./components/MessageForm_v1";
import { SEND_MESSAGE } from "./graphql/mutations";
import { GET_SENDER_ID } from "./graphql/queries";

export const NewMessage = () => {
  const featureFlag = useFlags(["segmented_message_warning"]);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const toast = useAvelaToast();
  const organization = useOrganization();
  const [searchParams] = useSearchParams();

  const [smsEditor, setSmsEditor] = useState<LexicalEditor>();
  const [emailEditor, setEmailEditor] = useState<LexicalEditor>();
  const emailEditorRef: EditorRef = (editor: LexicalEditor) => {
    if (editor != null) {
      setEmailEditor(editor);
    }
  };
  const smsEditorRef: EditorRef = (editor: LexicalEditor) => {
    if (editor != null) setSmsEditor(editor);
  };

  const state = location.state as NewMessageState;
  const organizationId = useMemo(
    () => organization.map((org) => org.id).withDefault(""),
    [organization]
  );

  const [formRecipients, isUnverifiedCommsEnabledForOrg] =
    useGetMessageRecipients(state?.formsIdList || []);

  const { remoteData: senderIdRemoteData } = useRemoteDataQuery<
    GQL.GetSenderId,
    GQL.GetSenderIdVariables
  >(GET_SENDER_ID, {
    variables: {
      userId: user.status === Status.OK ? user.data.id : "",
      organizationId: organizationId,
    },
    skip: user.status !== Status.OK || !organization.hasData(),
  });

  const [SendMessage] = useRemoteDataMutation<
    GQL.SendMessage,
    GQL.SendMessageVariables
  >(SEND_MESSAGE);

  const sendMessageInfo = useMemo(() => {
    let emailCount = 0;
    let smsCount = 0;
    const receiversInfos = formRecipients.flatMap(
      ({ formId, applicant, recipients }) =>
        recipients.flatMap((guardian) => {
          const contactInformation = Person.getContactInformation(
            guardian,
            isUnverifiedCommsEnabledForOrg
          );
          const type = contactInformation.type;
          const infos: GQL.message_adhoc_form_receiver_insert_input[] = [];

          if (
            type === Person.ContactType.Email ||
            type === Person.ContactType.All
          ) {
            emailCount++;
            infos.push({
              form_id: formId,
              applicant_id: applicant.id,
              receiver_person_id: guardian.id,
              receiver_contact_information: contactInformation.email_address,
              message_type: GQL.message_type_enum.email,
            });
          }

          if (
            type === Person.ContactType.Phone ||
            type === Person.ContactType.All
          ) {
            smsCount++;
            infos.push({
              form_id: formId,
              applicant_id: applicant.id,
              receiver_person_id: guardian.id,
              receiver_contact_information: contactInformation.phone_number,
              message_type: GQL.message_type_enum.SMS,
            });
          }

          return infos;
        })
    );

    return {
      receiversInfos,
      emailCount,
      smsCount,
      hasRecipientsWithEmail: emailCount > 0,
      hasRecipientsWithPhone: smsCount > 0,
    };
  }, [formRecipients, isUnverifiedCommsEnabledForOrg]);

  const recipients: RecipientsTableDataType[] = useMemo(
    () =>
      formRecipients.flatMap(({ applicant, recipients }) =>
        recipients.flatMap((recipient) => {
          const contact = Person.getContactInformation(
            recipient,
            isUnverifiedCommsEnabledForOrg
          );
          const base = {
            id: recipient.id,
            full_name: `${recipient.first_name ?? ""} ${
              recipient.last_name ?? ""
            }`.trim(),
            preferred_language: recipient.preferred_language,
            applicant_name: applicant.first_name ?? "",
          };

          switch (contact.type) {
            case Person.ContactType.Email:
              return [{ ...base, contact_info: contact.email_address }];
            case Person.ContactType.Phone:
              return [{ ...base, contact_info: contact.phone_number }];
            case Person.ContactType.All:
              return [
                { ...base, contact_info: contact.email_address },
                { ...base, contact_info: contact.phone_number },
              ];
            default:
              return [];
          }
        })
      ),
    [formRecipients, isUnverifiedCommsEnabledForOrg]
  );

  // Since we are dealing with dynamic verification, we need to build the schema based on the recipients info.
  const MessageSchema = useMemo(
    () =>
      generateMessageSchema(
        sendMessageInfo.hasRecipientsWithEmail,
        sendMessageInfo.hasRecipientsWithPhone
      ),
    [sendMessageInfo]
  );

  type MessageFormType = z.infer<typeof MessageSchema>;

  const { uploadRichContent } = useMessageRichContent(emailEditor, smsEditor);

  const handleSubmitMessage = useCallback(
    async (values: MessageFormType) => {
      try {
        if (!state?.formsIdList) {
          toast({
            title: "Error sending message",
            status: "error",
            isClosable: true,
            description: "Unable to send message without form IDs",
          });
          throw new Error("Unable to send message without form IDs");
        }
        const senderId = senderIdRemoteData
          .map((data) => data.person[0]?.id)
          .withDefault(undefined);
        if (!senderId) {
          toast({
            title: "Error sending message",
            status: "error",
            isClosable: true,
            description: "Unable to send message without senderId",
          });
          throw new Error("Unable to send message without senderId");
        }
        if (organizationId === "") {
          toast({
            title: "Error sending message",
            status: "error",
            isClosable: true,
            description: "Unable to send message without organization ID",
          });
          throw new Error("Unable to send message without organizationId");
        }

        const { document_id, messageDocument } = await uploadRichContent({
          subject: values.subject,
          language: "en",
          attachments: values.email_attachments,
        });

        if (!document_id || !messageDocument) {
          toast({
            title: "Error sending message",
            description: "There are no content to send",
          });
          return;
        }

        const messageObject: GQL.SendMessageVariables["message"] = {
          sender_person_id: senderId,
          organization_id: organizationId,
          message_adhoc_form_receivers: {
            data: sendMessageInfo.receiversInfos,
          },
          message_adhoc_payload_sms: {
            data: {
              message_body: messageDocument.smsText || "",
            },
          },
          message_adhoc_payload_email: {
            data: {
              message_body: messageDocument.text || "",
              message_subject: values.subject,
            },
          },
          message_adhoc_payload_rich_contents: {
            data: [
              {
                content_id: document_id,
              },
            ],
          },
        };

        await SendMessage({
          variables: {
            message: messageObject,
          },
        });

        toast({ title: "Messages sent" });
        navigate(-1);
      } catch (err) {
        console.error(err);
        toast.error({ title: "Error sending message" });
      }
    },
    [
      state?.formsIdList,
      senderIdRemoteData,
      organizationId,
      uploadRichContent,
      sendMessageInfo.receiversInfos,
      SendMessage,
      toast,
      navigate,
    ]
  );

  useEffect(() => {
    if (emailEditor && state?.emailBody) {
      const html = convertPlainTextToHtml(state.emailBody);
      importHtml(emailEditor, html);
    }

    if (smsEditor && state?.smsBody) {
      importPlainText(smsEditor, state.smsBody);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailEditor, smsEditor, state]);

  const initialValues = useMemo(() => {
    return {
      subject: "Message about your school form",
      email_body: "",
      email_attachments: [],
      sms_body: "",
    } satisfies MessageFormType;
  }, []);

  const { selectedNavFormTemplate } = useFormTemplates();

  const handleValidate = useCallback(
    async (values: MessageFormType) => {
      const formikError = new FormikErrorAccumulator<MessageFormType>();

      if (emailEditor) {
        if (!$validateEditorState(emailEditor)) {
          formikError.add({
            email_body:
              "One or more variables in the email content are invalid. Correct or remove them and try again.",
          });
        } else {
          formikError.add({
            email_body: mustacheSafeParse(
              editorStateToHtml(emailEditor),
              "One or more variables in the email content are invalid. Correct or remove them and try again."
            )[0],
          });
        }
      }

      if (values.subject) {
        formikError.add({
          subject: mustacheSafeParse(
            values.subject,
            "One or more variables in the email subject line are invalid. Correct or remove them and try again."
          )[0],
        });
      }

      if (smsEditor) {
        const plainTextSms = editorStateToPlainText(smsEditor);
        const segmentedMessage = toSegmentedMessage(plainTextSms, featureFlag);
        if (featureFlag["segmented_message_warning"].enabled) {
          if (segmentedMessage.isOverflowing()) {
            formikError.add({
              sms_body: `SMS message exceeds character limit.`,
            });
          }
        } else {
          if (segmentedMessage.numberOfCharacters > MESSAGE_CONTENT_LIMIT) {
            formikError.add({
              sms_body: `SMS message must be less than ${MESSAGE_CONTENT_LIMIT} characters.`,
            });
          }
        }
        if (!$validateEditorState(smsEditor)) {
          formikError.add({
            sms_body:
              "One or more variables in the SMS message are invalid. Correct or remove them and try again.",
          });
        } else {
          formikError.add({
            sms_body: mustacheSafeParse(
              plainTextSms,
              "One or more variables in the SMS message are invalid. Correct or remove them and try again."
            )[0],
          });
        }
      }

      let totalFileSize = 0; // This will be the sum of both inline images and attachments sizes in bytes.

      // Inline images
      if (emailEditor) {
        const htmlWithCIDs = editorStateToHtmlWithCIDs(emailEditor);
        const inlineSize =
          htmlWithCIDs?.attachments?.reduce((sum, attachment) => {
            const sizeInBase64 = attachment.data.length;
            const sizeInBytes = (sizeInBase64 * 3) / 4;
            return sum + sizeInBytes;
          }, 0) ?? 0;
        totalFileSize += inlineSize;
      }

      // Attachments
      const attachmentsSize = values.email_attachments
        ? sum(Array.from(values.email_attachments).map((file) => file.size))
        : 0;
      totalFileSize += attachmentsSize;

      if (totalFileSize > MAX_EMAIL_ATTACHMENTS_SIZE_MB * MB_SIZE) {
        formikError.add({
          email_attachments: `Total file size of inline images and attachments must be less than ${MAX_EMAIL_ATTACHMENTS_SIZE_MB} MB. Current total is ${(
            totalFileSize / MB_SIZE
          ).toFixed(1)}MB.`,
        });
      }

      const zodErrors = await validateWithZod(MessageSchema)(values);
      if (zodErrors) {
        formikError.add(zodErrors);
      }

      return formikError.errors;
    },
    [MessageSchema, emailEditor, featureFlag, smsEditor]
  );

  if (!state) return <NotFound></NotFound>;

  return (
    <Flex direction="column">
      <Breadcrumb
        mb={4}
        items={breadcrumb.form.getBreadcrumbsForSendMessage(
          organization,
          selectedNavFormTemplate,
          searchParams.toString()
        )}
      />

      <Heading as="h1" size="lg" pb={8}>
        Send message
      </Heading>

      <Flex>
        <Box width="50%">
          <RecipientsTable recipients={recipients} />
        </Box>

        <Flex direction="column" pl={6} width="50%">
          <Formik<MessageFormType>
            initialValues={initialValues}
            onSubmit={handleSubmitMessage}
            validate={handleValidate}
            validateOnMount={false}
            validateOnChange={false}
          >
            {({ isValid, isSubmitting, validateForm, submitForm }) => (
              <Flex direction="column" as={Form}>
                <Text variant="filterHeader" mb={2}>
                  Messages
                </Text>
                <Flex gap={2} mb={2}>
                  <Tag
                    bg="purple.500"
                    color="white"
                    alignSelf="flex-start"
                    size="sm"
                    minWidth="60px"
                  >
                    <TagLeftIcon as={RiLightbulbLine} />
                    <TagLabel>Tip</TagLabel>
                  </Tag>
                  <Text>
                    Type <Code>{"{{"}</Code> in any content area to see
                    variables. Variables help you replace text with dynamic
                    content.
                  </Text>
                </Flex>

                <MessageForm
                  hasRecipientsWithEmail={
                    sendMessageInfo.hasRecipientsWithEmail
                  }
                  hasRecipientsWithPhone={
                    sendMessageInfo.hasRecipientsWithPhone
                  }
                  emailEditorRef={emailEditorRef}
                  smsEditorRef={smsEditorRef}
                />
                <Flex gap={2} marginLeft="auto" mt={2}>
                  <Button
                    onClick={() => navigate(-1)}
                    variant="outline"
                    colorScheme="gray"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isSubmitting}
                    onClick={() => validateForm()}
                  >
                    Send
                  </Button>
                </Flex>
              </Flex>
            )}
          </Formik>
        </Flex>
      </Flex>
    </Flex>
  );
};
